import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { LITRES_PER_GALLON } from 'utils/unitConverter/conversionFactors'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from 'utils/unitConverter/types'

type TFertigationVolumeUnit = 'gal' | 'l'

const DEFAULT_DECIMAL_PLACES = 0
const LITRES_PER_UNIT: Record<TFertigationVolumeUnit, number> = { gal: LITRES_PER_GALLON, l: 1 }

export const fertigationVolume: Record<
  'fertigationVolume',
  (
    volume?: number | null,
    options?: TUnitConverterOptionsWithUnits<TFertigationVolumeUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  fertigationVolume: (
    volume: number | null = null,
    {
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'gal',
      outputUnit = userDetailsStore.getState().fertigation === 'IMPERIAL' ? 'gal' : 'l',
    }: TUnitConverterOptionsWithUnits<TFertigationVolumeUnit> = {},
  ) => {
    const convertedValue =
      isNil(volume) || inputUnit === outputUnit
        ? volume
        : (volume * LITRES_PER_UNIT[inputUnit]) / LITRES_PER_UNIT[outputUnit]

    const measurement = outputUnit === 'gal' ? translate.measurements.gallons : translate.measurements.liters
    const unitSymbol = measurement.unit()
    const label = translate.phrases.banyanApp('Fertigation volume')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: () => (isNil(convertedValue) ? null : +convertedValue.toFixed(decimalPlaces)),
      valueAsString: () => measurement.value(convertedValue, decimalPlaces),
      valueWithSuffix: () => measurement.valueWithUnit(convertedValue, decimalPlaces),
      valueWithNoRounding: () => convertedValue,
    }
  },
}
